import React, { Component, createRef } from 'react'
import { observer } from 'mobx-react'
import {
  getLocalStorage,
  setLocalStorage,
  i18nTranslate,
  getNestedConfigValue,
  trackGTMEvents,
  getFssaiLicenceTypeLabel,
  getFssaiExpiryDuration,
} from 'src/utils'
import { setupFocusTrap } from 'src/utils/focusTrap'
import { pageNames } from 'src/routes/pathParams'
import { Button, Modal } from 'react-bootstrap'
import { GrClose } from 'react-icons/gr'
import { customerContainer } from 'src/models'
import { FSSAI_MODAL_KEY, licenseTypes } from './fixture'
import './styles.scss'

@observer
class FssaiExpiryModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      isOpen: false,
      expiryDate: '',
      licenseType: '',
      isExpired: false,
      daysDifference: 0,
    }
    this.modalRef = createRef()
    this.prevAccountType = ''
  }

  componentDidMount() {
    const lastDismissed = getLocalStorage(FSSAI_MODAL_KEY)
    // Store initial account type for comparison
    this.prevAccountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''
    if (!lastDismissed) {
      this.initializeModalState()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const lastDismissed = getLocalStorage(FSSAI_MODAL_KEY)
    const { isOpen } = this.state
    const currentAccountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''

    // Check if account type changed (e.g., conversion from RC to BA)
    if (
      !lastDismissed &&
      !isOpen &&
      currentAccountType &&
      this.prevAccountType !== currentAccountType &&
      customerContainer?.isBrandAffiliate(currentAccountType)
    ) {
      // Update prevAccountType before calling initializeModalState
      this.prevAccountType = currentAccountType
      this.initializeModalState()
      return
    }
    // Store current account type for next comparison
    this.prevAccountType = currentAccountType
  }

  initializeModalState = () => {
    const accountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''
    const properties =
      customerContainer?.profileResponse?.accounts?.properties || {}
    let licenseExpiration = ''
    let certificateAuthority = ''
    if (customerContainer?.isBrandAffiliate(accountType)) {
      properties.forEach(item => {
        if (item.attributeId === 'licenseExpiration')
          licenseExpiration = item.value
        if (item.attributeId === 'certificateAuthority')
          certificateAuthority = item.value
      })
      if (licenseExpiration && !certificateAuthority) {
        certificateAuthority = 'FSSAI' // Default to FSSAI if expiration is provided without authority
      }
      if (!licenseExpiration && !certificateAuthority) {
        const noFassiExpiryDays =
          getNestedConfigValue('fssaiNotificaionConfig', 'noFassiExpiryDays') ||
          90
        certificateAuthority = 'none'
        let createdTime =
          customerContainer?.profileResponse?.accounts?.createdTime
        licenseExpiration = new Date(createdTime).setDate(
          new Date(createdTime).getDate() + noFassiExpiryDays
        ) // Set expiry date to configured days from account creation if license expiration is not provided
      }
      let expiryDate = new Date(licenseExpiration)
      const { expiry, daysDifference } = this.getExpiryDetails(expiryDate)
      const daysToShowPopUp = this.getPopUpTOShowDetails(certificateAuthority)

      if (daysDifference <= daysToShowPopUp) {
        this.setState({
          isOpen: true,
          expiryDate: expiry,
          licenseType: certificateAuthority,
          isExpired: daysDifference < 0,
          daysDifference,
        })
        this.trackFssaiPopup('view', certificateAuthority, daysDifference)
      }
    }
  }

  getPopUpTOShowDetails = licenseType => {
    let configKey = ''
    if (licenseType === 'FSSAI') {
      configKey = 'popupWindowforAutualFssai'
    } else if (licenseType === 'Ack-FSSAI') {
      configKey = 'popupWindowforAckFssai'
    } else {
      configKey = 'popupWindowforNoFssai'
    }
    return getNestedConfigValue('fssaiNotificaionConfig', configKey) || 30
  }

  getExpiryDetails = expiryDate => {
    const currentDate = new Date()
    const expiryDateObj = new Date(expiryDate)
    const timeDiff = expiryDateObj.getTime() - currentDate.getTime()
    const daysDifference = Math.ceil(timeDiff / (1000 * 3600 * 24))
    const expiryDay = expiryDateObj.getDate()
    const expiryMonth = expiryDateObj.getMonth() + 1
    const expiryYear = expiryDateObj.getFullYear()
    const formattedExpiryDate = `${expiryDay}/${expiryMonth}/${expiryYear}`
    return { expiry: formattedExpiryDate, daysDifference }
  }

  trackFssaiPopup = (status, licenseType, daysDifference) => {
    const accountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''
    const userId = customerContainer?.profileResponse?.referenceAttribute1 || ''
    let event = ''
    if (status === 'view') {
      event = 'fssai_expiry_popup_view'
    } else if (status === 'dismiss') {
      event = 'fssai_expiry_popup_dismiss'
    } else if (status === 'gotoprofile') {
      event = 'fssai_expiry_popup_profile'
    }
    trackGTMEvents({
      event,
      id: userId,
      account_type: accountType,
      licence_type: getFssaiLicenceTypeLabel(licenseType),
      expiry_duration: getFssaiExpiryDuration(daysDifference),
      expiry_limitations:
        'purchases of Pharmanex products will be limited to 2 units per SKU per month for personal use, and commission payouts will be temporarily paused',
    })
  }

  handleGoToProfile = () => {
    setLocalStorage(FSSAI_MODAL_KEY, true)
    this.trackFssaiPopup(
      'gotoprofile',
      this.state.licenseType,
      this.state.daysDifference
    )
    this.setState({ isOpen: false })
    this.props.history.push(pageNames.accountSettings)
  }

  handleClose = () => {
    this.trackFssaiPopup(
      'dismiss',
      this.state.licenseType,
      this.state.daysDifference
    )
    setLocalStorage(FSSAI_MODAL_KEY, true)
    this.setState({ isOpen: false })
  }

  getModalTitle = () => {
    const { licenseType, isExpired } = this.state

    if (licenseType === 'none') {
      return isExpired
        ? i18nTranslate(
            'fssai.modal.noneTitleUpdated',
            'No FSSAI Number Updated'
          )
        : i18nTranslate(
            'fssai.modal.noneTitleEntered',
            'No FSSAI Number Entered'
          )
    }

    if (isExpired) {
      return licenseType === 'FSSAI'
        ? i18nTranslate(
            'fssai.modal.expiredTitle',
            'Your FSSAI License has Expired'
          )
        : i18nTranslate(
            'fssai.modal.ackExpiredTitle',
            'Your Acknowledgement Number Has Expired'
          )
    }

    return licenseType === 'FSSAI'
      ? i18nTranslate(
          'fssai.modal.expiringSoonTitle',
          'Your FSSAI License Is Expiring Soon'
        )
      : i18nTranslate(
          'fssai.modal.ackExpiringSoonTitle',
          'Your Acknowledgement Number Is Expiring Soon'
        )
  }

  render() {
    const { isOpen, expiryDate, licenseType, isExpired } = this.state
    const currentAccountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''
    const isBAUser = customerContainer?.isBrandAffiliate(currentAccountType)
    if (!isOpen || !isBAUser) return null

    const currentLicense = licenseTypes.find(item => item.type === licenseType)
    return (
      <Modal
        show={isOpen}
        onHide={this.handleClose}
        aria-labelledby="fssai-modal-title"
        aria-label={i18nTranslate(
          'fssai.modal.ariaLabel',
          'FSSAI License Expiry Notification'
        )}
        centered
        className="fssai-modal-content"
        backdropClassName="fssai-modal-overlay">
        <Modal.Header
          closeLabel={i18nTranslate('fssai.modal.close')}
          className="fssai-modal-header">
          <Modal.Title
            id="fssai-modal-title"
            tabIndex={0}
            className="fssai-modal-title">
            {this.getModalTitle()}
          </Modal.Title>
          <div className="ssm-close-icon">
            <GrClose
              tabIndex={0}
              aria-label="close button"
              role="button"
              onClick={() => this.handleClose()}
            />
          </div>
        </Modal.Header>
        <Modal.Body
          className="fssai-modal-body"
          tabIndex={0}
          ref={this.modalRef}
          aria-label={i18nTranslate(
            'fssai.modal.bodyAria',
            'FSSAI Expiry Modal Content'
          )}>
          {currentLicense && !isExpired && (
            <>
              <p>
                {i18nTranslate(
                  currentLicense.titleKeyExpire,
                  currentLicense.titleDefaultExpire
                )}{' '}
                {currentLicense.type === 'FSSAI' && (
                  <strong>{expiryDate}.</strong>
                )}{' '}
                {i18nTranslate(
                  currentLicense.renewalTextKey,
                  currentLicense.renewalTextDefault
                )}
              </p>
              <p>
                {i18nTranslate(
                  currentLicense.consequencesTitleKey,
                  currentLicense.consequencesTitleDefault
                )}
              </p>
              <ul
                aria-label={i18nTranslate(
                  'fssai.modal.listAria',
                  'Consequences of license expiration'
                )}>
                <li>
                  {i18nTranslate(
                    'fssai.modal.consequence1',
                    'You will not be permitted to sell food or nutritional products.'
                  )}
                </li>
                <li>
                  {i18nTranslate(
                    'fssai.modal.consequence2',
                    'Purchases will be limited to personal use (maximum 5 units per SKU per month).'
                  )}
                </li>
                <li>
                  {i18nTranslate(
                    'fssai.modal.consequence3',
                    "Commissions may be limited or temporarily held at the company's discretion."
                  )}
                </li>
              </ul>
              <p>
                {i18nTranslate(
                  'fssai.modal.updateLicense',
                  'Please update your license details to avoid these restrictions.'
                )}
              </p>
            </>
          )}
          {currentLicense && isExpired && (
            <p>
              {i18nTranslate(
                currentLicense.expiredMessageKey,
                currentLicense.expiredMessageDefault
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="fssai-modal-cta mt-3"
            onClick={this.handleGoToProfile}
            aria-label={i18nTranslate('fssai.modal.ctaAria', 'Go to Profile')}>
            {i18nTranslate('fssai.modal.cta', 'Go to Profile')}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default FssaiExpiryModal
